// frame: xwUX2(我的订阅)/ JAcFj(分享) @ ui/dogdisk-user.pen — /#/files?view=shares 双 tab
// 顶部 topRow(segmented 文件/资源包 + 搜索/传输/通知)由 files.tsx 承担,
// 本组件从 sectionHeader(rydYO)开始渲染:胶囊 tab 切换 + 排序按钮 + 封面卡片列表。
// tab 从 URL query 读:?tab=mine → 分享,否则默认订阅;setTab 写回 URL(保留 view=shares)。
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowUpDown, Plus } from 'lucide-react'
import { Spinner, apiClient, useCurrentUser, cn } from '@dogdisk/shared'
import {
  packToCard,
  subscriptionToCard,
  useMyPacks,
  useMySubscriptions,
  type Pack,
  type PackCard,
} from '../_lib/pack-api'
import { DEFAULT_SORT, type SortState } from '../_lib/file-sort'
import { PackCoverThumb } from './pack-cover-thumb'
import { DeleteConfirmDialog } from './modals/delete-confirm-dialog'
import { ShareActionSheet } from './modals/share-action-sheet'
import { SortFilterSheet } from './modals/sort-filter-sheet'
import { QrCodeModal } from './modals/qr-code-modal'

type Tab = 'subscriptions' | 'mine'

type ActionTarget = {
  card: PackCard
  /** 仅分享 tab(owner 动作需要);订阅 tab 不传 */
  status?: Pack['status']
  subtitle?: string
}

function sortCards(cards: PackCard[], { sortBy, order }: SortState): PackCard[] {
  const dir = order === 'asc' ? 1 : -1
  return [...cards].sort((a, b) => {
    const cmp =
      sortBy === 'name'
        ? a.name.localeCompare(b.name)
        : a.sortKey.localeCompare(b.sortKey) // ISO 时间串,字典序即时间序
    return cmp * dir
  })
}

export function ShareList() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const qc = useQueryClient()
  const { status } = useCurrentUser()
  const isAuth = status === 'authenticated'

  const tab: Tab = params.get('tab') === 'mine' ? 'mine' : 'subscriptions'
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params)
    if (t === 'mine') next.set('tab', 'mine')
    else next.delete('tab')
    setParams(next, { replace: true })
  }

  const subsQuery = useMySubscriptions()
  const packsQuery = useMyPacks()

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const [sortOpen, setSortOpen] = useState(false)
  const [actionFor, setActionFor] = useState<ActionTarget | null>(null)
  // 2026-05-29 接入 mdYih 二次确认 — 取消分享(BQOCz/ondv5 cancelAction 复用 mdYih)
  const [cancelConfirm, setCancelConfirm] = useState<PackCard | null>(null)
  const [qrFor, setQrFor] = useState<PackCard | null>(null)

  const cancelMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/gapi/user/packs/${id}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pack', 'mine'] })
      setActionFor(null)
    },
  })
  const restoreMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`/gapi/user/packs/${id}/restore`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pack', 'mine'] })
      setActionFor(null)
    },
  })

  // 后端 pack status 实际枚举 'active'/'deleted';mine tab 只显示 active
  const rawCards: PackCard[] =
    tab === 'subscriptions'
      ? (subsQuery.data ?? []).map(subscriptionToCard)
      : (packsQuery.data ?? []).filter((p) => p.status === 'active').map(packToCard)
  const cards = sortCards(rawCards, sort)

  const loading =
    status === 'loading' ||
    (isAuth && (tab === 'subscriptions' ? subsQuery.isLoading : packsQuery.isLoading))

  const openActionsFor = (c: PackCard) => {
    if (tab === 'subscriptions') {
      setActionFor({ card: c })
      return
    }
    const pack = (packsQuery.data ?? []).find((p) => p.id === c.id)
    setActionFor({
      card: c,
      // mine tab 只渲染 active 包;回查竞态找不到时兜底 active,避免 owner sheet 误显"恢复分享"
      status: pack?.status ?? 'active',
      ...(pack?.view_count != null && { subtitle: `${pack.view_count}次访问` }),
    })
  }

  return (
    <>
      {/* sectionHeader(rydYO):左 胶囊 tab 切换,右 排序按钮 */}
      <div data-testid="rydYO" className="flex items-center justify-between px-6 py-3">
        <div className="flex rounded-[18px] bg-[#F6F7F8] p-1">
          <button
            type="button"
            data-testid="shares-tab-subscriptions"
            onClick={() => setTab('subscriptions')}
            className={cn(
              'rounded-[14px] px-4 py-1.5 text-[14px]',
              tab === 'subscriptions'
                ? 'bg-white font-semibold text-[#1A1A1A] shadow-sm'
                : 'text-[#9CA3AF]',
            )}
          >
            我的订阅
          </button>
          <button
            type="button"
            data-testid="shares-tab-mine"
            onClick={() => setTab('mine')}
            className={cn(
              'rounded-[14px] px-4 py-1.5 text-[14px]',
              tab === 'mine' ? 'bg-white font-semibold text-[#1A1A1A] shadow-sm' : 'text-[#9CA3AF]',
            )}
          >
            分享
          </button>
        </div>
        <button
          type="button"
          data-testid="shares-sort-btn"
          onClick={() => setSortOpen(true)}
          className="flex items-center gap-1 text-[13px] text-[#9CA3AF]"
        >
          <ArrowUpDown size={14} aria-hidden="true" />
          排序
        </button>
      </div>

      <ul className="flex flex-col gap-2 px-6 pb-safe-tab">
        {loading ? (
          <li className="flex justify-center py-8"><Spinner className="text-gray-400" /></li>
        ) : cards.length === 0 ? (
          <li className="flex flex-col items-center gap-3 py-12 text-center text-[13px] text-[#9CA3AF]">
            {tab === 'subscriptions' ? '暂无订阅' : '暂无分享'}
            {tab === 'mine' && isAuth && (
              <button
                type="button"
                onClick={() => navigate('/pack/create')}
                className="rounded-lg bg-[#19caad] px-4 py-2 text-[13px] font-semibold text-white"
              >
                立即创建
              </button>
            )}
          </li>
        ) : (
          cards.map((c) => (
            <li key={c.id} className="flex h-[72px] items-center gap-3.5 rounded-2xl bg-[#F6F7F8] p-4">
              <button
                type="button"
                data-testid={`pack-card-${c.id}`}
                onClick={() => navigate(tab === 'mine' ? `/pack/manage/${c.id}` : `/pack/${c.id}`)}
                className="flex min-w-0 flex-1 items-center gap-3.5 text-left"
              >
                <PackCoverThumb packId={c.id} coverUrl={c.coverThumbUrl} />
                <span className="min-w-0 flex-1 truncate text-[19px] font-semibold text-[#1A1A1A]">
                  {c.name}
                </span>
              </button>
              <button
                type="button"
                onClick={() => openActionsFor(c)}
                className="-mr-1 flex h-8 w-6 items-center justify-center text-[#9CA3AF]"
                aria-label="更多"
              >
                <svg width="4" height="16" viewBox="0 0 4 16" fill="currentColor"><circle cx="2" cy="2" r="1.5" /><circle cx="2" cy="8" r="1.5" /><circle cx="2" cy="14" r="1.5" /></svg>
              </button>
            </li>
          ))
        )}
      </ul>

      {/* JAcFj Vusl9:分享 tab 浮动创建按钮(容器 max-w-[402px] 内水平定位) */}
      {tab === 'mine' && isAuth && (
        <div
          className="pointer-events-none fixed inset-x-0 z-20 mx-auto flex max-w-[402px] justify-end px-6"
          style={{ bottom: 'calc(100px + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            data-testid="shares-create-pack-btn"
            onClick={() => navigate('/pack/create')}
            className="pointer-events-auto flex h-12 items-center gap-2 rounded-3xl bg-[#FF6B6B] px-5 text-[14px] font-semibold text-white shadow-[0_6px_16px_rgba(255,107,107,0.25)]"
          >
            <Plus size={18} aria-hidden="true" />
            创建资源包
          </button>
        </div>
      )}

      {!isAuth && status !== 'loading' && (
        <div
          className="fixed inset-x-0 z-20 mx-auto flex max-w-[402px] gap-2 px-6"
          style={{ bottom: 'calc(80px + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={() => navigate('/auth/login')}
            className="h-11 flex-1 rounded-lg bg-[#19caad] text-[14px] font-semibold text-white shadow-lg"
          >
            登录后管理我的分享
          </button>
        </div>
      )}

      <SortFilterSheet
        open={sortOpen}
        onClose={() => setSortOpen(false)}
        value={sort}
        onChange={setSort}
        options={['created_at', 'name']}
      />

      {actionFor && (
        <ShareActionSheet
          open
          onClose={() => setActionFor(null)}
          variant={tab === 'subscriptions' ? 'subscriber' : 'owner'}
          shareId={actionFor.card.id}
          shareLink={`${window.location.origin}/#/s/${actionFor.card.id}`}
          packName={actionFor.card.name}
          {...(actionFor.subtitle && { subtitle: actionFor.subtitle })}
          {...(actionFor.status && { status: actionFor.status })}
          onCancelShare={() => {
            const card = actionFor.card
            setActionFor(null)
            setCancelConfirm(card)
          }}
          onRestoreShare={() => restoreMut.mutate(actionFor.card.id)}
          onShowQr={() => {
            const card = actionFor.card
            setActionFor(null)
            setQrFor(card)
          }}
        />
      )}

      <QrCodeModal
        open={qrFor !== null}
        onClose={() => setQrFor(null)}
        text={qrFor ? `${window.location.origin}/#/s/${qrFor.id}` : ''}
      />

      {/* mdYih 二次确认 — ondv5 cancelAction 触发(2026-05-29) */}
      <DeleteConfirmDialog
        open={cancelConfirm !== null}
        onClose={() => setCancelConfirm(null)}
        onConfirm={() => {
          if (cancelConfirm) cancelMut.mutate(cancelConfirm.id)
          setCancelConfirm(null)
        }}
        title="取消分享?"
        subtitle="取消后分享链接将立即失效,接收方无法继续访问。"
        confirmText="确认取消"
        loading={cancelMut.isPending}
      />
    </>
  )
}
